import { Injectable } from '@angular/core';
import { AppUserRole } from '../models/enums/appUserRole.enum';
import { AppUserCompanyAccessType } from '../models/enums/appUserCompanyAccessType.enum';
import { Category } from '../models/enums/category.enum';
import { CompanyAccess } from '../models/companyAccess';

@Injectable({
  providedIn: 'root'
})
export class UserMappingService {
  private readonly roleMapping: { [key: string]: AppUserRole } = {
    'Administrator': AppUserRole.Admin,
    'Leader': AppUserRole.Leader,
    'User': AppUserRole.User,
    'Customer': AppUserRole.Customer
  };

  private readonly roleReverseMapping: { [key: number]: string } = {
    [AppUserRole.Admin]: 'Administrator',
    [AppUserRole.Leader]: 'Leader',
    [AppUserRole.User]: 'User',
    [AppUserRole.Customer]: 'Customer'
  };

  private readonly categoryMapping: { [key: string]: Category } = {
    'None': Category.None,
    'Audit': Category.Audit,
    'Corporate governance': Category.CorporateGovernance,
    'CSR': Category.Csr,
    'Voting results': Category.VotingResults,
    'Compensation': Category.Compensation
  };

  private readonly categoryReverseMapping: { [key: number]: string } = {
    [Category.None]: 'None',
    [Category.Audit]: 'Audit',
    [Category.CorporateGovernance]: 'Corporate governance',
    [Category.Csr]: 'CSR',
    [Category.VotingResults]: 'Voting results',
    [Category.Compensation]: 'Compensation',
    [Category.Accounting]: 'Audit'
  };

  private readonly companyAccessMapping: { [key: string]: AppUserCompanyAccessType } = {
    'Details': AppUserCompanyAccessType.Details,
    'Collection': AppUserCompanyAccessType.Collection,
    'Audit 1': AppUserCompanyAccessType.Audit1,
    'Audit 2': AppUserCompanyAccessType.Audit2,
    'Board': AppUserCompanyAccessType.Board,
    'Committees': AppUserCompanyAccessType.Committees,
    'Ownership': AppUserCompanyAccessType.Ownership,
    'Family': AppUserCompanyAccessType.Family,
    'Salary 1': AppUserCompanyAccessType.Salary1,
    'Salary 2': AppUserCompanyAccessType.Salary2,
    'Accounting': AppUserCompanyAccessType.Accounting,
    'Society': AppUserCompanyAccessType.Society,
    'Employees': AppUserCompanyAccessType.Employees,
    'Energy & Water': AppUserCompanyAccessType.EnergyWater,
    'Emissions & Waste': AppUserCompanyAccessType.EmissionsWaste,
    'Voting Results': AppUserCompanyAccessType.VotingResults,
    'Messages': AppUserCompanyAccessType.Messages,
    'Status': AppUserCompanyAccessType.Process
  };

  mapRoleToEnum(displayName: string): AppUserRole {
    return this.roleMapping[displayName] || AppUserRole.User;
  }

  mapRoleToDisplayName(enumValue: AppUserRole): string {
    return this.roleReverseMapping[enumValue] || 'User';
  }

  mapCompanyAccessToEnums(tabs: CompanyAccess[]): AppUserCompanyAccessType[] {
    return tabs.map(tab => this.companyAccessMapping[tab.name] || AppUserCompanyAccessType.None);
  }

  /**
   * Read-only tabs travel as names. The backend stores them as a separate bitmask, masked against
   * the granted tabs, and rejects writes to any tab listed here.
   */
  mapTabNamesToEnums(names: string[]): AppUserCompanyAccessType[] {
    return names.map(name => this.companyAccessMapping[name] || AppUserCompanyAccessType.None);
  }

  mapEnumsToTabNames(enums: AppUserCompanyAccessType[]): string[] {
    return enums.map(enumValue => this.mapCompanyAccessToDisplayName(enumValue));
  }

  mapEnumsToCompanyAccess(enums: AppUserCompanyAccessType[]): CompanyAccess[] {
    return enums.map(enumValue => ({ name: this.mapCompanyAccessToDisplayName(enumValue) }));
  }

  mapCompanyAccessToDisplayName(enumValue: AppUserCompanyAccessType): string {
    const entry = Object.entries(this.companyAccessMapping).find(([_, value]) => value === enumValue);
    return entry ? entry[0] : 'None';
  }

  mapCategoryToEnum(displayName: string): Category {
    return this.categoryMapping[displayName] || Category.None;
  }

  mapCategoryToDisplayName(enumValue: Category): string {
    return this.categoryReverseMapping[enumValue] || 'None';
  }
}
